import { useContext, useEffect, useState } from 'react'
import { FontIcon, Stack, TextField } from '@fluentui/react'
import { SendRegular } from '@fluentui/react-icons'
import { Select, MenuItem } from '@mui/material'
import type { SelectChangeEvent } from '@mui/material/Select'
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded'

import Send from '../../assets/Send.svg'

import styles from './QuestionInput.module.css'
import { ChatMessage } from '../../api'
import { AppStateContext } from '../../state/AppProvider'
import { resizeImage } from '../../utils/resizeImage'
import { CubeBadgeIcon } from '../common/CubeBadgeIcon'
import { CubeOption } from '../../api'

interface Props {
  onSend: (question: ChatMessage['content'], id?: string) => void
  disabled: boolean
  placeholder?: string
  clearOnSend?: boolean
  conversationId?: string
  cubeOptions?: CubeOption[]
  onCubeChange?: (cubeValue: string) => void
}

export const QuestionInput = ({ onSend, disabled, placeholder, clearOnSend, conversationId, cubeOptions = [], onCubeChange }: Props) => {
  const [question, setQuestion] = useState<string>('')
  const [base64Image, setBase64Image] = useState<string | null>(null);
  const [selectedCube, setSelectedCube] = useState<string>('')

  const appStateContext = useContext(AppStateContext)
  const OYD_ENABLED = appStateContext?.state.frontendSettings?.oyd_enabled || true;

  // Set initial selected cube when options load
  useEffect(() => {
    if (cubeOptions.length > 0 && !selectedCube) {
      const defaultValue = cubeOptions[0].value
      setSelectedCube(defaultValue)
      onCubeChange?.(defaultValue)
    }
  }, [cubeOptions])

  const handleCubeChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value
    setSelectedCube(value)
    onCubeChange?.(value)
  }

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (file) {
      await convertToBase64(file);
    }
  };

  const convertToBase64 = async (file: Blob) => {
    try {
      const resizedBase64 = await resizeImage(file, 800, 800);
      setBase64Image(resizedBase64);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const sendQuestion = () => {
    if (disabled || !question.trim()) {
      return
    }

    const questionTest: ChatMessage["content"] = base64Image ? [{ type: "text", text: question }, { type: "image_url", image_url: { url: base64Image } }] : question.toString();

    if (conversationId && questionTest !== undefined) {
      onSend(questionTest, conversationId)
      setBase64Image(null)
    } else {
      onSend(questionTest)
      setBase64Image(null)
    }

    if (clearOnSend) {
      setQuestion('')
    }
  }

  const onEnterPress = (ev: React.KeyboardEvent<Element>) => {
    if (ev.key === 'Enter' && !ev.shiftKey && !(ev.nativeEvent?.isComposing === true)) {
      ev.preventDefault()
      sendQuestion()
    }
  }

  const onQuestionChange = (_ev: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
    setQuestion(newValue || '')
  }

  const sendQuestionDisabled = disabled || !question.trim()

  return (
    <Stack horizontal className={styles.questionInputContainer}>
      <TextField
        className={styles.questionInputTextArea}
        placeholder={placeholder}
        multiline
        resizable={false}
        borderless
        value={question}
        onChange={onQuestionChange}
        onKeyDown={onEnterPress}
      />
      {!OYD_ENABLED && (
        <div className={styles.fileInputContainer}>
          <input
            type="file"
            id="fileInput"
            onChange={(event) => handleImageUpload(event)}
            accept="image/*"
            className={styles.fileInput}
          />
          <label htmlFor="fileInput" className={styles.fileLabel} aria-label='Upload Image'>
            <FontIcon
              className={styles.fileIcon}
              iconName={'PhotoCollection'}
              aria-label='Upload Image'
            />
          </label>
        </div>)}
      {base64Image && <img className={styles.uploadedImage} src={base64Image} alt="Uploaded Preview" />}
      <div className={styles.cubeDropdownContainer}>
        <Select
          value={selectedCube}
          onChange={handleCubeChange}
          IconComponent={KeyboardArrowDownRoundedIcon}
          size="small"
          variant="standard"
          disableUnderline
          aria-label="Select NSR Cube"
          renderValue={(value) => {
            const opt = cubeOptions.find(o => o.value === value)
            return (
              <>
                <CubeBadgeIcon size={20} />
                <span>{opt?.label || value}</span>
              </>
            )
          }}
          MenuProps={{
            anchorOrigin: { vertical: 'top', horizontal: 'right' },
            transformOrigin: { vertical: 'bottom', horizontal: 'right' },
          }}
          sx={{
            display: 'flex',
            padding: '2px',
            alignItems: 'center',
            gap: '4px',
            borderRadius: '6px',
            border: 'none',
            outline: 'none',
            boxShadow: 'none',
            background: '#f1f1f1',
            fontSize: '11px',
            fontWeight: 600,
            color: '#4a4a4a',
            lineHeight: '14px',
            minWidth: 0,
            '&:hover': { background: '#e9e9e9ff' },
            '&:focus, &:focus-visible, &.Mui-focused': {
              outline: 'none',
              boxShadow: 'none',
            },
            '&:active': {
              background: '#f1f1f1',
            },
            '&::before, &::after': {
              display: 'none',
            },
            '.MuiSelect-select': {
              padding: '2px 20px 2px 4px !important',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              cursor: 'pointer',
            },
            '.MuiSelect-select:focus': {
              backgroundColor: 'transparent',
            },
            '.MuiSelect-icon': {
              fontSize: '13px',
              color: '#4a4a4a',
              right: '2px',
              marginLeft: '6px',
              pointerEvents: 'none',
            },
          }}
        >
          {cubeOptions.map((option) => (
            <MenuItem
              key={option.value}
              value={option.value}
              sx={{
                fontSize: '12px',
                color: '#4a4a4a',
                fontWeight: option.value === selectedCube ? 600 : 400,
                background: option.value === selectedCube ? '#edf5fd' : 'transparent',
                '&:hover': { background: '#f5f5f5' },
                '&.Mui-selected': { background: '#edf5fd' },
                '&.Mui-selected:hover': { background: '#e3eefa' },
              }}
            >
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </div>
      <div
        className={styles.questionInputSendButtonContainer}
        role="button"
        tabIndex={0}
        aria-label="Ask question button"
        onClick={sendQuestion}
        onKeyDown={e => (e.key === 'Enter' || e.key === ' ' ? sendQuestion() : null)}>
        {sendQuestionDisabled ? (
          <SendRegular className={styles.questionInputSendButtonDisabled} />
        ) : (
          <img src={Send} className={styles.questionInputSendButton} alt="Send Button" />
        )}
      </div>
      <div className={styles.questionInputBottomBorder} />
    </Stack>
  )
}
